// Requests for Tilastot seinällä (statistics_view.v1). Paths come from API_ENDPOINTS (config/api.js).
import { useCallback, useEffect, useState } from 'react';
import API_ENDPOINTS from '../../config/api';
import useContentRefresh from '../../hooks/useContentRefresh';
import { getOrCreateParticipantId } from '../../utils/participantId';
import socketService from '../../../../../packages/shared/socket';

export const RESOURCE = 'statistics-view';

/** JSON request with the session cookie; throws the server's error message and code. */
export async function request(url, { method = 'GET', body } = {}) {
  const res = await fetch(url, {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error?.message || (typeof data.error === 'string' ? data.error : `HTTP ${res.status}`));
    err.code = data.error?.code;
    throw err;
  }
  return data;
}

/** The values of one table for a choice's fixed dimensions: [{ dims, period, value, flag }]. */
export async function fetchValues(instanceId, tableId, fixed = {}) {
  // The endpoint may be a relative path behind the dev proxy, so no URL object here.
  const query = fixed && Object.keys(fixed).length ? `?dims=${encodeURIComponent(JSON.stringify(fixed))}` : '';
  const { values } = await request(`${API_ENDPOINTS.INSTANCE_SERIES_TABLE(instanceId, tableId)}${query}`);
  return values;
}

export const saveChoice = (instanceId, choice) => request(API_ENDPOINTS.STATISTICS_VIEW(instanceId), { method: 'PUT', body: choice });

/**
 * The module's state — the instance's tables and the facilitator's choice —
 * reloaded when the facilitator changes it (instance-content-changed,
 * resource 'statistics-view').
 */
export function useStatisticsView(instanceId) {
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    if (!instanceId) return;
    try {
      setState(await request(API_ENDPOINTS.STATISTICS_VIEW(instanceId)));
      setError(null);
    } catch (e) {
      setError(e);
    }
  }, [instanceId]);
  // Joins the instance's room before useContentRefresh subscribes: that hook
  // listens only on a socket that already exists.
  useEffect(() => {
    if (instanceId) socketService.connect(instanceId, 'display', `statistics-view-${getOrCreateParticipantId() || Date.now()}`);
  }, [instanceId]);
  useEffect(() => { load(); }, [load]);
  useContentRefresh(instanceId, [RESOURCE], load, { minIntervalMs: 500, enabled: Boolean(instanceId) });
  return { state, error, reload: load };
}
